import type { CardReadOperationReceipt } from "@ascleo/frontdesk-contracts/cardreader/operations";
import { CardReadOperationRpcs } from "@ascleo/frontdesk-contracts/cardreader/rpc";
import { BookingQuestionnaireRpcs } from "@ascleo/frontdesk-contracts/booking-questionnaire/rpc";
import type { BookingReviewRequest } from "@ascleo/frontdesk-contracts/booking-questionnaire/runtime";
import type { BookingRuntimeRequest, BookingSlotsRequest, BookingSubmissionRequest } from "@ascleo/frontdesk-contracts/booking-questionnaire/runtime";
import { FrontDeskSchedulingRpcs } from "@ascleo/frontdesk-contracts/scheduling/rpc";
import type { BookingStatusRequest } from "@ascleo/frontdesk-contracts/scheduling/recovery";
/**
 * Doc-Cirrus Bridge RPC Client
 *
 * Self-contained RPC client for FrontDesk+ self-service operations.
 * Communicates directly with doc-cirrus-bridge server via Effect RPC.
 *
 * Pattern follows @ascleo/client/lib/kzbv-rpc.client.ts
 */

import { RpcClient } from "effect/rpc";
import {
  EebAvailability,
  EebConsentPresentation,
  EebKioskConsentAcceptance,
  EebKioskConsentRequest,
  EebKioskProblem,
} from "@ascleo/frontdesk-contracts/eeb/kiosk";
import { Cause, Clock, Deferred, Exit, Effect, Result as Either, Layer, Schema as S } from "effect";
import { DocCirrusBridgeRpcs } from "@ascleo/doc-cirrus-bridge/rpc";
import { reportOperationalError } from "../operational-error-telemetry";
import { CardWorkflowLifecycle } from "./card-workflow-lifecycle";
import {
  configureBridgeHttpTransport,
  configureBridgeUrlSecurity,
  createConfiguredBridgeFetch,
  createDocCirrusRpcLayer,
  getBridgeUrl,
  getDocCirrusRpcLayer,
  setBridgeUrl,
  setSolToken,
  type BridgeUrlSecurityPolicy,
} from "./bridge-rpc.transport";

const BACKGROUND_POLLING_OPERATIONS = new Set([
  "connectivity.status",
  "frontdesk.getCards",
]);

class EmptyCardResultError extends Error {
  readonly _tag = "CardDataUnavailableError";
  readonly reason = "empty-payload";
  readonly terminalId: string;
  constructor(terminalId: string) {
    super("inSuite returned no patient data for the inserted card");
    this.terminalId = terminalId;
  }
}

const CARD_READ_TIMEOUT_MS = 60_000;
const activeCardReads = new Set<string>();
const cardWorkflows = new CardWorkflowLifecycle((params) => runRpcOperation(
  "frontdesk.cancelCardRead",
  Effect.scoped(Effect.gen(function* () {
    const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
    return yield* client.FrontDeskCancelCardRead(params);
  })).pipe(Effect.provide(getDocCirrusRpcLayer(CARD_READ_TIMEOUT_MS))),
));

async function runCardReadOperation<Result, Failure>(
  operation: string,
  terminalId: string,
  effect: Effect.Effect<Result, Failure, never>,
): Promise<Result> {
  const key = JSON.stringify([getBridgeUrl(), terminalId]);
  if (activeCardReads.has(key)) {
    throw new Error("A card read is already in progress for this terminal");
  }
  activeCardReads.add(key);
  try {
    return await runRpcOperation(operation, effect);
  } finally {
    activeCardReads.delete(key);
  }
}

const privateBookingOperations = new Set([
  "frontdesk.beginBookingContext", "frontdesk.getBookingQuestionnaire", "frontdesk.getBookingRouteAvailability",
  "frontdesk.getQuestionnaireSlots", "frontdesk.getBookingReview", "frontdesk.submitQuestionnaireBooking", "frontdesk.revokeBookingContext",
  "frontdesk.getBookingStatus",
]);

function runRpcOperation<Result, Failure>(
  operation: string,
  effect: Effect.Effect<Result, Failure, never>,
): Promise<Result> {
  const startedAt = performance.now();
  return Effect.runPromiseExit(effect).then((exit) => {
    if (Exit.isSuccess(exit)) return exit.value;
    const error = Cause.squash(exit.cause);
    if (!BACKGROUND_POLLING_OPERATIONS.has(operation)) {
      void reportOperationalError({
        action: operation,
        severity: "error",
        source: "rpc",
        transport: "effect-rpc-http",
        error: privateBookingOperations.has(operation) ? new Error("Booking operation failed") : error,
        interrupted: Cause.hasInterruptsOnly(exit.cause),
        durationMs: Math.round(performance.now() - startedAt),
      });
    }
    throw error;
  });
}

async function runEebHttp<A, I>(
  operation: string,
  path: string,
  schema: S.Codec<A, I>,
  init?: RequestInit,
): Promise<A> {
  try {
    const bridgeFetch = createConfiguredBridgeFetch();
    const response = await bridgeFetch(`${getBridgeUrl()}${path}`, init);
    const raw: unknown = await response.json();

    if (!response.ok) {
      const decodedProblem = S.decodeUnknownResult(EebKioskProblem)(raw);
      throw new Error(
        Either.isSuccess(decodedProblem)
          ? decodedProblem.success.title
          : "Die eEB-Anfrage ist fehlgeschlagen.",
      );
    }

    return await Effect.runPromise(S.decodeUnknownEffect(schema)(raw));
  } catch (error) {
    void reportOperationalError({
      action: operation,
      severity: "error",
      source: "rpc",
      transport: "effect-http",
      error,
    });
    throw error;
  }
}

function eebHttpPath(
  endpoint: "consent-presentation" | "requests" | "availability",
): string {
  const pathname = new URL(getBridgeUrl()).pathname.replace(/\/$/, "");
  if (
    pathname.endsWith("/api/frontdesk/demo") ||
    pathname.endsWith("/api/frontdesk/preview")
  ) {
    return `/eeb/${endpoint}`;
  }
  if (pathname.endsWith("/api/frontdesk")) {
    return `/proxy/api/frontdesk/eeb/${endpoint}`;
  }
  return `/api/frontdesk/eeb/${endpoint}`;
}

/**
 * Doc-Cirrus Bridge RPC Client
 *
 * Provides client-side access to doc-cirrus-bridge operations for self-service.
 * All methods are async/await wrappers around Effect-based RPC calls.
 *
 * @example
 * ```ts
 * // Check connectivity
 * const status = await docCirrusRpcClient.connectivity.status()
 *
 * // List patients
 * const patients = await docCirrusRpcClient.patient.list({ lastname: 'Schmidt' })
 * ```
 */
export const docCirrusRpcClient = {
  /**
   * Connectivity operations
   */
  connectivity: {
    /**
     * Check connection status to doc-cirrus inSuite
     */
    status: async () => {
      return runRpcOperation(
        "connectivity.status",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.ConnectivityStatus({});
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },
  },

  /**
   * Patient operations
   */
  patient: {
    /**
     * List patients with optional filters
     */
    list: async (params: {
      firstname?: string;
      lastname?: string;
      dob?: string;
      patientNo?: string;
      limit?: number;
      skip?: number;
    }) => {
      return runRpcOperation(
        "patient.list",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.PatientList(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Find a patient by ID
     */
    find: async (params: { _id: string }) => {
      return runRpcOperation(
        "patient.find",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.PatientFind(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },
  },

  /**
   * Card reader operations
   *
   * Note: These methods use the doc-cirrus-bridge's complex TI connector parameters.
   * For simplified frontdesk operations, use the frontdesk-specific RPCs.
   */
  cardreader: {
    /**
     * Get available cards from connected readers
     */
    getCards: async (params: { terminalId: string }) => {
      return runRpcOperation(
        "cardreader.getCards",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.CardReaderGetCards(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Get telematic infrastructure info (terminals and cards)
     */
    getTi: async (params?: {
      name?: string;
      ti?: boolean;
      page?: number;
      itemsPerPage?: number;
      noCountLimit?: "true" | "false";
    }) => {
      return runRpcOperation(
        "cardreader.getTi",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.CardReaderGetTi({
              name: params?.name ?? "",
              ti: params?.ti ?? true,
              page: params?.page ?? 0,
              itemsPerPage: params?.itemsPerPage ?? 1000,
              noCountLimit: params?.noCountLimit ?? "false",
            });
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Get telematic infrastructure settings
     */
    getTiSettings: async () => {
      return runRpcOperation(
        "cardreader.getTiSettings",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.CardReaderGetTiSettings({});
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Get patient data from card in terminal
     */
    getPatientFromTerminal: async (params: { terminalId: string }) => {
      return runCardReadOperation(
        "cardreader.getPatientFromTerminal",
        params.terminalId,
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.CardReaderGetPatientFromTerminal(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer(CARD_READ_TIMEOUT_MS))),
      );
    },
  },

  /**
   * Schedule operations
   */
  schedule: {
    /**
     * List schedules with optional filters
     */
    list: async (params?: {
      calendar?: string;
      patient?: string;
      start_gte?: string;
      end_lte?: string;
      sort?: string;
      page?: number;
      itemsPerPage?: number;
    }) => {
      return runRpcOperation(
        "schedule.list",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.ScheduleList(params ?? {});
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Get available time slots for appointment booking
     */
    getAvailableTimeSlots: async (params: {
      startDate: string;
      endDate: string;
      scheduleTypeId: string;
    }) => {
      return runRpcOperation(
        "schedule.getAvailableTimeSlots",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.ScheduleGetAvailableTimeSlots(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },
  },

  /**
   * Schedule type operations
   */
  scheduletype: {
    /**
     * List schedule types (appointment types)
     * Note: Requires all parameters as per the doc-cirrus-bridge schema
     */
    list: async (params: {
      sort: string;
      page: number;
      itemsPerPage: number;
      noCountLimit: "true" | "false";
      creationDate: string;
      creationDate_lt: string;
      creationDate_gt: string;
    }) => {
      return runRpcOperation(
        "scheduletype.list",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.ScheduleTypeList(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * List schedule types with default pagination parameters
     */
    listAll: async () => {
      return runRpcOperation(
        "scheduletype.listAll",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.ScheduleTypeList({
              sort: "_id",
              page: 0,
              itemsPerPage: 1000,
              noCountLimit: "false",
              creationDate: "",
              creationDate_lt: "",
              creationDate_gt: "",
            });
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },
  },

  /**
   * FrontDesk operations
   *
   * High-level, workflow-aware operations for the self-service kiosk.
   * These provide simplified interfaces compared to raw CardReader operations.
   */
  frontdesk: {
    /** Resolve the public location for one already-confirmed appointment. */
    getAppointmentPrintLocation: async (params: {
      scheduleId: string;
      expectedPatientId: string;
    }) => {
      return runRpcOperation(
        "frontdesk.getAppointmentPrintLocation",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskGetAppointmentPrintLocation(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer(3_500))),
      );
    },
    /**
     * Check connectivity status for frontdesk kiosk
     * Includes API, Telematik, card reader, and medical card status.
     */
    connectionStatus: async (params: { terminalId: string }) => {
      return runRpcOperation(
        "frontdesk.connectionStatus",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskConnectionStatus(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Get patient cards (EGK) currently inserted in terminal
     * Returns only patient cards, filtered from professional cards.
     */
    getCards: async (params: { terminalId: string }) => {
      return runRpcOperation(
        "frontdesk.getCards",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskGetCards(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Read card from terminal and initiate workflow
     * Returns CardReadWorkflowResult with status and possible actions.
     */
    readCard: async (params: { terminalId: string }, options?: { readonly signal?: AbortSignal }) => {
      if (options?.signal?.aborted) throw new Error("Card read session has ended");
      const readLayer = getDocCirrusRpcLayer(CARD_READ_TIMEOUT_MS);
      const cleanupLayer = getDocCirrusRpcLayer();
      const aborted = Effect.callback<never, Error>((resume) => {
        const signal = options?.signal;
        const stop = () => resume(Effect.fail(new Error("Card read session has ended")));
        if (signal?.aborted) stop();
        else signal?.addEventListener("abort", stop, { once: true });
        return Effect.sync(() => signal?.removeEventListener("abort", stop));
      });
      const result = await runCardReadOperation(
        "frontdesk.readCard",
        params.terminalId,
        Effect.scoped(
          Effect.gen(function* () {
            yield* Effect.tryPromise(() => cardWorkflows.waitForCleanup());
            const started = yield* Clock.currentTimeMillis;
            const client = yield* RpcClient.make(CardReadOperationRpcs);
            if (options?.signal?.aborted) return yield* Effect.fail(new Error("Card read session has ended"));
            let receipt: CardReadOperationReceipt | undefined;
            let ended = false;
            let delivered = false;
            let cleanupStarted = false;
            const abandon = () => Effect.suspend(() => {
              if (!receipt || cleanupStarted) return Effect.void;
              cleanupStarted = true;
              const operationHandle = receipt.operationHandle;
              return Effect.scoped(Effect.gen(function* () {
                const cleanupClient = yield* RpcClient.make(CardReadOperationRpcs);
                yield* cleanupClient.FrontDeskAbandonCardReadOperation({ operationHandle });
              })).pipe(Effect.provide(cleanupLayer), Effect.catchCause(() => Effect.sync(() => {
                void reportOperationalError({ action: "card-reader.abandon-operation", severity: "error", source: "lifecycle", error: new Error("Card cancellation could not be confirmed") });
              })));
            });
            const began = yield* Deferred.make<CardReadOperationReceipt, unknown>();
            // Keep receipt delivery alive after the screen leaves, so a late capability is cleaned
            // up once. This owns only a bounded Bridge request, never a second physical read.
            yield* Effect.forkDetach(Effect.scoped(Effect.gen(function* () {
              const beginClient = yield* RpcClient.make(CardReadOperationRpcs);
              const issued = yield* beginClient.FrontDeskBeginCardRead(params);
              receipt = issued;
              return issued;
            })).pipe(Effect.provide(readLayer), Effect.timeout(CARD_READ_TIMEOUT_MS), Effect.exit,
              Effect.flatMap((outcome) => Deferred.done(began, outcome)),
              Effect.ensuring(Effect.suspend(() => ended && !delivered ? abandon() : Effect.void))));
            return yield* Effect.gen(function* () {
              const issued = yield* Deferred.await(began);
              while (true) {
                if (options?.signal?.aborted) return yield* Effect.fail(new Error("Card read session has ended"));
                const remaining = Math.min(CARD_READ_TIMEOUT_MS, issued.timeoutMs) - ((yield* Clock.currentTimeMillis) - started);
                if (remaining <= 0) return yield* Effect.fail(new Error("Card read resolution timed out"));
                const state = yield* client.FrontDeskGetCardReadStatus({ operationHandle: issued.operationHandle }).pipe(Effect.timeout(remaining));
                if (state.status === "ready") {
                  if (options?.signal?.aborted) return yield* Effect.fail(new Error("Card read session has ended"));
                  delivered = true; return state.result;
                }
                if (state.status === "unavailable") {
                  return yield* Effect.fail(state.reason === "empty-payload"
                    ? new EmptyCardResultError(params.terminalId)
                    : new Error(`Card read unavailable: ${state.reason}`));
                }
                if (state.status !== "pending") return yield* Effect.fail(new Error("Card read was cancelled or abandoned"));
                yield* Effect.sleep(Math.min(1_000, remaining));
              }
            }).pipe(Effect.raceFirst(aborted), Effect.ensuring(Effect.gen(function* () {
              ended = true;
              if (!delivered) yield* Effect.forkDetach(abandon());
            })));
          }),
        ).pipe(Effect.provide(readLayer)),
      );
      cardWorkflows.remember(result);
      if (result.status !== "CANCELLED" && result.validationStatus !== "OK") {
        await cardWorkflows.abandon(result.crlogId);
        throw new Error("Card validation does not permit check-in; contact reception");
      }
      return result;
    },

    /**
     * Release a pending operation when the patient leaves its flow; never reread the card.
     * @since 1.1.10
     * @example
     * await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: "read-1" });
     */
    abandonCardRead: async (params: { crlogId: string }) => {
      try {
        await cardWorkflows.abandon(params.crlogId);
      } catch (error) {
        void reportOperationalError({
          action: "card-reader.abandon", severity: "error", source: "lifecycle",
          component: "CardWorkflowLifecycle", error,
        });
      }
    },

    /**
     * Apply card data (confirm merged changes)
     */
    applyCardData: async (params: { crlogId: string }) => {
      return cardWorkflows.runAction(params.crlogId, "apply", () => runRpcOperation(
        "frontdesk.applyCardData",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskApplyCardData(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer(CARD_READ_TIMEOUT_MS))),
      ));
    },

    /**
     * Cancel card read workflow
     */
    cancelCardRead: async (params: { crlogId: string }) => {
      return cardWorkflows.runAction(params.crlogId, "cancel", () => runRpcOperation(
        "frontdesk.cancelCardRead",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskCancelCardRead(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer(CARD_READ_TIMEOUT_MS))),
      ));
    },

    /**
     * Select patient when multiple matches found
     */
    selectPatient: async (params: { crlogId: string; patientId: string }) => {
      return cardWorkflows.runAction(params.crlogId, "matching_select", () => runRpcOperation(
        "frontdesk.selectPatient",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskSelectPatient(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer(CARD_READ_TIMEOUT_MS))),
      ));
    },

    /**
     * Get available appointment types for scheduling
     */
    getAppointmentTypes: async (params?: { calendarId?: string }) => {
      return runRpcOperation(
        "frontdesk.getAppointmentTypes",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(FrontDeskSchedulingRpcs);
            return yield* client.FrontDeskGetAppointmentTypes(params ?? {});
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Get available time slots for an appointment type
     */
    getAvailableTimeSlots: async (params: {
      appointmentTypeId: string;
      startDate: string;
      endDate: string;
    }) => {
      return runRpcOperation(
        "frontdesk.getAvailableTimeSlots",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(FrontDeskSchedulingRpcs);
            return yield* client.FrontDeskGetAvailableTimeSlots(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /** Begin revision consistency after patient identification.
     * @since 1.1.8
     * @example await docCirrusRpcClient.frontdesk.beginBookingContext("patient");
     */
    beginBookingContext: (patientId: string) => runRpcOperation("frontdesk.beginBookingContext", Effect.scoped(Effect.gen(function* () {
      const client = yield* RpcClient.make(BookingQuestionnaireRpcs);
      return yield* client.FrontDeskBeginBookingContext({ patientId });
    })).pipe(Effect.provide(getDocCirrusRpcLayer()))),
    /** Read the patient-safe projection for a transient answer path.
     * @since 1.1.8
     * @example await docCirrusRpcClient.frontdesk.getBookingQuestionnaire(request);
     */
    getBookingQuestionnaire: (request: BookingRuntimeRequest) => runRpcOperation("frontdesk.getBookingQuestionnaire", Effect.scoped(Effect.gen(function* () {
      const client = yield* RpcClient.make(BookingQuestionnaireRpcs);
      return yield* client.FrontDeskGetBookingQuestionnaire(request);
    })).pipe(Effect.provide(getDocCirrusRpcLayer()))),
    /** Read the authoritative stage only after complete primary evaluation.
     * @since 1.1.8
     * @example await docCirrusRpcClient.frontdesk.getBookingRouteAvailability(request);
     */
    getBookingRouteAvailability: (request: BookingRuntimeRequest) => runRpcOperation("frontdesk.getBookingRouteAvailability", Effect.scoped(Effect.gen(function* () {
      const client = yield* RpcClient.make(BookingQuestionnaireRpcs);
      return yield* client.FrontDeskGetBookingRouteAvailability(request);
    })).pipe(Effect.provide(getDocCirrusRpcLayer()))),
    /** Request current-route slots constrained by the pinned server-evaluated questionnaire route.
     * @since 1.1.8
     * @example await docCirrusRpcClient.frontdesk.getQuestionnaireSlots(request);
     */
    getQuestionnaireSlots: (request: BookingSlotsRequest) => runRpcOperation("frontdesk.getQuestionnaireSlots", Effect.scoped(Effect.gen(function* () {
      const client = yield* RpcClient.make(BookingQuestionnaireRpcs);
      return yield* client.FrontDeskGetQuestionnaireSlots(request);
    })).pipe(Effect.provide(getDocCirrusRpcLayer()))),
    /** Read the server-owned patient summary without preparing or dispatching a booking.
     * @since 1.1.8
     * @example await docCirrusRpcClient.frontdesk.getBookingReview(request);
     */
    getBookingReview: (request: BookingReviewRequest) => runRpcOperation("frontdesk.getBookingReview", Effect.scoped(Effect.gen(function* () {
      const client = yield* RpcClient.make(BookingQuestionnaireRpcs);
      return yield* client.FrontDeskGetBookingReview(request);
    })).pipe(Effect.provide(getDocCirrusRpcLayer()))),
    /** Submit only server-finalized content through durable receipt fencing.
     * @since 1.1.8
     * @example await docCirrusRpcClient.frontdesk.submitQuestionnaireBooking(request);
     */
    submitQuestionnaireBooking: (request: BookingSubmissionRequest) => runRpcOperation("frontdesk.submitQuestionnaireBooking", Effect.scoped(Effect.gen(function* () {
      const client = yield* RpcClient.make(BookingQuestionnaireRpcs);
      return yield* client.FrontDeskSubmitQuestionnaireBooking(request);
    })).pipe(Effect.provide(getDocCirrusRpcLayer()), Effect.result)).then((outcome) => {
      if (Either.isFailure(outcome)) {
        void reportOperationalError({ action: "frontdesk.submitQuestionnaireBooking", severity: "error", source: "rpc", transport: "effect-rpc-http", error: new Error("Booking submission failed") });
        throw outcome.failure;
      }
      return outcome.success;
    }),
    /** Revoke context on completion/reset without touching recovery receipts.
     * @since 1.1.8
     * @example await docCirrusRpcClient.frontdesk.revokeBookingContext(handle);
     */
    revokeBookingContext: (contextHandle: string) => runRpcOperation("frontdesk.revokeBookingContext", Effect.scoped(Effect.gen(function* () {
      const client = yield* RpcClient.make(BookingQuestionnaireRpcs);
      return yield* client.FrontDeskRevokeBookingContext({ contextHandle });
    })).pipe(Effect.provide(getDocCirrusRpcLayer()))),

    /** Recover a retained submission using its receipt before any booking retry.
     * @since 1.1.8
     * @example await docCirrusRpcClient.frontdesk.getBookingStatus(request);
     */
    getBookingStatus: async (params: BookingStatusRequest) => runRpcOperation(
      "frontdesk.getBookingStatus",
      Effect.scoped(Effect.gen(function* () {
        const client = yield* RpcClient.make(FrontDeskSchedulingRpcs);
        return yield* client.FrontDeskGetBookingStatus(params);
      })).pipe(Effect.provide(getDocCirrusRpcLayer())),
    ),

    /**
     * Search for patient using privacy-safe demographic search
     * Returns FOUND, AMBIGUOUS, or NOT_FOUND status
     */
    searchPatient: async (params: {
      lastname: string;
      dob: string;
      firstname?: string;
    }) => {
      return runRpcOperation(
        "frontdesk.searchPatient",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskSearchPatient(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Fetch per-device preflight checklist inputs. The caller derives the
     * ordered positions via the shared `derivePreflightChecklist`.
     */
    getDeviceChecklist: async (params: {
      deviceIdHash: string;
      terminalId: string;
    }) => {
      return runRpcOperation(
        "frontdesk.getDeviceChecklist",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskGetDeviceChecklist(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Check in patient to their appointment
     * Finds today's appointments and marks arrival
     */
    checkIn: async (params: { patientId: string; scheduleId?: string }) => {
      return runRpcOperation(
        "frontdesk.checkIn",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskCheckIn(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Get practice KIM account for eEB communication
     */
    getKimAccount: async () => {
      return runRpcOperation(
        "frontdesk.getKimAccount",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskGetKimAccount({});
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Search statutory (GKV) insurers by name
     * Returns matching insurers with IKNR and name
     */
    searchInsurers: async (params: { query: string }) => {
      return runRpcOperation(
        "frontdesk.searchInsurers",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskSearchInsurers(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Get insurer by IK number
     * Returns insurer details including phone number for contact
     */
    getInsurerByIk: async (params: { iknr: string }) => {
      return runRpcOperation(
        "frontdesk.getInsurerByIk",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskGetInsurerByIk(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Check if patient has valid GKV coverage for current quarter
     * Returns coverage status and previous insurer info for pre-fill
     */
    checkQuarterlyCoverage: async (params: { patientId: string }) => {
      return runRpcOperation(
        "frontdesk.checkQuarterlyCoverage",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskCheckQuarterlyCoverage(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Whether the server can accept an eEB consent at all right now.
     *
     * Asked before the patient is offered the eEB path, so a practice that is
     * not ready never shows consent screens that must then fail. Any error
     * answers `false`: not offering eEB costs a patient nothing, while walking
     * them into an unacceptable request costs them a dead end.
     */
    isEebAvailable: async (): Promise<boolean> => {
      try {
        const result = await runEebHttp(
          "frontdesk.isEebAvailable",
          eebHttpPath("availability"),
          EebAvailability,
        );
        return result.available;
      } catch {
        return false;
      }
    },

    /**
     * Loads the server-authoritative practice facts and exact consent wording.
     */
    getEebConsentPresentation: (params: { patientId: string }) =>
      runEebHttp(
        "frontdesk.getEebConsentPresentation",
        `${eebHttpPath("consent-presentation")}?patientId=${encodeURIComponent(params.patientId)}`,
        EebConsentPresentation,
      ),

    /**
     * Persists an idempotent eEB consent/request on the dedicated signed route.
     */
    recordEebConsent: async (params: EebKioskConsentRequest) => {
      const input = await Effect.runPromise(
        S.decodeUnknownEffect(EebKioskConsentRequest)(params),
      );
      return runEebHttp(
        "frontdesk.recordEebConsent",
        eebHttpPath("requests"),
        EebKioskConsentAcceptance,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(input),
        },
      );
    },

    /**
     * List active prescriptions for a patient
     * Returns prescription summaries filtered by active status
     */
    listPrescriptions: async (params: {
      patientId: string;
      fromDate?: string;
      actTypes?: Array<
        | "PUBPRESCR"
        | "PRIVPRESCR"
        | "LONGPRESCR"
        | "PRESCRBTM"
        | "PRESCRG"
        | "PRESCRT"
      >;
    }) => {
      return runRpcOperation(
        "frontdesk.listPrescriptions",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskListPrescriptions(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Get prescription detail with linked medications
     * Returns full prescription info including medication details
     */
    getPrescriptionDetail: async (params: { id: string }) => {
      return runRpcOperation(
        "frontdesk.getPrescriptionDetail",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskGetPrescriptionDetail(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },

    /**
     * Get medication history for a patient
     * Returns list of medications from the last 365 days
     */
    getMedicationHistory: async (params: { patientId: string }) => {
      return runRpcOperation(
        "frontdesk.getMedicationHistory",
        Effect.scoped(
          Effect.gen(function* () {
            const client = yield* RpcClient.make(DocCirrusBridgeRpcs);
            return yield* client.FrontDeskGetMedicationHistory(params);
          }),
        ).pipe(Effect.provide(getDocCirrusRpcLayer())),
      );
    },
  },
};

// Re-export for advanced Effect usage
export { DocCirrusBridgeRpcs, RpcClient, Effect, Layer };
export {
  configureBridgeHttpTransport,
  configureBridgeUrlSecurity,
  createDocCirrusRpcLayer,
  getBridgeUrl,
  setBridgeUrl,
  setSolToken,
};
export type { BridgeUrlSecurityPolicy };
